import { t } from '@/lib/i18n';
import { createFileRoute } from '@tanstack/react-router';
import { PageContainer, ProCard } from '@ant-design/pro-components';
import { message, Typography } from 'antd';
import { ChangePasswordForm } from '@/features/auth/password/change-form';
import { useRedirectNavigation } from '@/features/auth/use-redirect-navigation';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { sanitizeRedirect } from '@/lib/routes';

/**
 * The one screen a signed-in account with an initial password may use.
 *
 * It sits under `_authenticated` — the session is required — but the layout's forced-change
 * gate lets this path through, and this page touches nothing but the native
 * `POST /api/auth/change-password`: every other business route answers 403
 * `PASSWORD_CHANGE_REQUIRED` until the change succeeds.
 *
 * `redirect` is where the operator was headed when the gate stopped them. It is sanitized like
 * every other redirect target (same-origin paths only) and followed through
 * `useRedirectNavigation`, which maps it back onto real route literals.
 */
export const Route = createFileRoute('/_authenticated/set-password')({
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: sanitizeRedirect(search.redirect),
  }),
  component: SetPasswordPage,
});

function SetPasswordPage() {
  useDocumentTitle(t('设置新密码'));
  const { session } = Route.useRouteContext();
  const { redirect: redirectTo } = Route.useSearch();
  const goToRedirect = useRedirectNavigation();
  const mustChangePassword = session.user.mustChangePassword === true;

  const form = (
    <ChangePasswordForm
      mustChangePassword={mustChangePassword}
      onChanged={async () => {
        message.success(t('密码已更新，其它设备上的会话已失效'));
        await goToRedirect(redirectTo);
      }}
    />
  );

  if (mustChangePassword) {
    return (
      <ProCard variant="outlined" className="p-2 sm:p-3">
        <section aria-labelledby="password-setup-title">
          <header className="mb-6">
            <Typography.Title
              level={3}
              id="password-setup-title"
              className="mt-0 mb-2"
            >
              {t('设置新密码')}
            </Typography.Title>
            <Typography.Paragraph type="secondary" className="mb-0">
              {t('首次登录，请先修改管理员提供的初始密码')}
            </Typography.Paragraph>
          </header>
          {form}
        </section>
      </ProCard>
    );
  }

  return (
    <PageContainer
      header={{
        title: t('设置新密码'),
        subTitle: t('修改当前账号的密码'),
      }}
    >
      <ProCard variant="outlined">{form}</ProCard>
    </PageContainer>
  );
}
